import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, Typography, App } from 'antd';
import SceneAnnotateCard from './SceneAnnotateCard';
import { ForeignSyncTaskListCard } from './ForeignSyncTaskListCard';
import ExistingCompletedModal, { type ExistingCompletedChoice } from './ExistingCompletedModal';
import BatchGenerateCard from './BatchGenerateCard';
import ScanJiraCard from './ScanJiraCard';
import SceneGenJobList from './SceneGenJobList';
import PerceptionIngestCard from './PerceptionIngestCard';
import { parseSceneGenerationTab } from './sceneGenerationDomain';
import { useCurrentUser } from '@/common/auth/adminAcl';
import { MAX_BATCH_CASES, batchLimitMessage } from '@/constants/sceneBatch';
import {
  batchAddScenesToTree,
  cancelSceneBatch,
  checkExistingCompletedScenes,
  createSceneFromJira,
  createSceneFromCaseFlow,
  getCreateSceneFromCaseFlowStatus,
  getCreateSceneFromJiraStatus,
  resumeSceneBatch,
  scanJiraDaysAndCreateScenes,
  getScanJiraDaysStatus,
  getSceneTree,
  type CreateSceneFromJiraState,
  type CreateSceneFromCaseFlowState,
  type ExistingCompletedScene,
  type SceneGenMode,
  type ScanJiraDaysState,
  type SceneTreeNode,
} from '@/apis/simulation';

const { Title, Text } = Typography;

/** 两张批量建场景卡的状态是同一套结构（Jira / Case-Flow 只是字段名不同）。 */
type BatchState = CreateSceneFromJiraState | CreateSceneFromCaseFlowState;

const SceneGenerationPage: React.FC = () => {
  const { message } = App.useApp();
  const { hasPerm } = useCurrentUser();
  const canManageSecurity = hasPerm('data:confidential:manage');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseSceneGenerationTab(searchParams.get('tab'));
  const [pncJobRefresh, setPncJobRefresh] = useState(0);
  const [perceptionJobRefresh, setPerceptionJobRefresh] = useState(0);
  const refreshPncJobs = useCallback(() => setPncJobRefresh(n => n + 1), []);

  // 全量场景列表已下线,搬到 /scene-management 默认视图;相关 state 一并清理。

  // 场景批量生成:内嵌 Jira / Case-Flow 二选一 tab,默认 Case-Flow
  // (v3 决策:不支持混合模式;两种来源共用 保密 / 目标场景集 选项)。
  // 「立即构建」复选框已下线:创建场景一律调度 CF 构建(autoGenerate 恒 true),
  // 不再给用户「只建不构建」的选项。
  const [batchSource, setBatchSource] = useState<'jira' | 'caseflow'>('caseflow');
  // 场景生成模式(Jira / Case-Flow 共用),默认事故场景
  const [genMode, setGenMode] = useState<SceneGenMode>('generate_and_qc_check');

  const [jiraKey, setJiraKey] = useState('');
  const [skipLogsimCheck] = useState(true);
  // 保密开关：默认不勾；仅 data:confidential:manage 权限用户可见。勾选后新建
  // 场景的 security_level=1，仅具备 data:confidential:view 的角色可见。
  const [confidential, setConfidential] = useState(false);
  // 目标场景集：生成完成进入「质检中」后自动入集；默认不选（undefined）。
  const [sceneSetNodeId, setSceneSetNodeId] = useState<number | undefined>(undefined);
  const [sceneTree, setSceneTree] = useState<SceneTreeNode[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [scanDays, setScanDays] = useState(7);
  // 自动扫描的 Jira 项目多选（AIP / DC / GC）；默认只选 AIP，保持与老行为一致。
  // 后端在 projects=[] 时会退化到默认 AIP，前端点扫描前也做 UX 保护（禁用按钮）。
  const [scanProjects, setScanProjects] = useState<string[]>(['AIP']);
  const [scanSubmitting, setScanSubmitting] = useState(false);
  const [scanState, setScanState] = useState<ScanJiraDaysState | null>(null);
  const [scanStateLoading, setScanStateLoading] = useState(false);
  const [createState, setCreateState] = useState<CreateSceneFromJiraState | null>(null);
  const [createStateLoading, setCreateStateLoading] = useState(false);

  const fetchCreateState = useCallback(async (silent = false) => {
    if (!silent) setCreateStateLoading(true);
    try {
      const st = await getCreateSceneFromJiraStatus();
      setCreateState(st ?? { status: 'idle' });
      return st ?? { status: 'idle' as const };
    } finally {
      if (!silent) setCreateStateLoading(false);
    }
  }, []);

  // 提交前「已生成完成场景」弹框：非空即打开，resolve 由弹框按钮回调兑现
  const [existingPrompt, setExistingPrompt] = useState<{
    items: ExistingCompletedScene[];
    canJoinSet: boolean;
    resolve: (choice: ExistingCompletedChoice) => void;
  } | null>(null);

  /**
   * 批量生成提交前检查：输入 case 中有已完成 / 瑕疵品场景时弹框让用户选择。
   * 返回最终要提交的 case 列表；null = 放弃本次提交（取消 / 检查或入集失败）。
   * 忽略 / 忽略并加入集合 会把剩余 case 回写输入框。
   */
  const resolveExistingCompleted = async (
    codes: string[],
    sceneSetId: number | undefined,
    setInput: (v: string) => void,
  ): Promise<string[] | null> => {
    let items: ExistingCompletedScene[];
    try {
      items = await checkExistingCompletedScenes(codes, genMode);
    } catch {
      return null; // 错误提示已由 axios 拦截器弹出
    }
    if (items.length === 0) return codes;

    const choice = await new Promise<ExistingCompletedChoice>(resolve => {
      setExistingPrompt({ items, canJoinSet: sceneSetId != null, resolve });
    });
    if (choice === 'cancel') return null;
    if (choice === 'regenerate') return codes;

    if (choice === 'ignoreAndJoin' && sceneSetId != null) {
      try {
        const res = await batchAddScenesToTree(sceneSetId, items.map(it => it.sceneId));
        const rejected = res.rejected?.length ?? 0;
        message.success(
          `已加入场景集 ${res.added} 个` +
          (res.skipped ? `，${res.skipped} 个已在集合中` : '') +
          (rejected ? `，${rejected} 个不合格被拒` : ''),
        );
      } catch {
        return null; // 入集失败：不裁剪输入，便于用户重试
      }
    }
    const done = new Set(items.map(it => it.code));
    const remaining = codes.filter(c => !done.has(c));
    setInput(remaining.join('\n'));
    if (remaining.length === 0) {
      message.info('剩余 case 均已生成完成，无需提交生成');
      return null;
    }
    return remaining;
  };

  const handleSubmit = async () => {
    const inputKeys = jiraKey.split(/[\s,;;，]+/).map(k => k.trim()).filter(Boolean);
    if (inputKeys.length === 0) {
      message.warning('请输入 Jira Issue Key');
      return;
    }
    if (inputKeys.length > MAX_BATCH_CASES) {
      message.error(batchLimitMessage(inputKeys.length));
      return;
    }
    setSubmitting(true);
    try {
      const keys = await resolveExistingCompleted(inputKeys, sceneSetNodeId, setJiraKey);
      if (!keys) return;
      const keysStr = keys.join(',');
      const resp = await createSceneFromJira({
        jiraIssueKeys: keysStr,
        skipLogsimCheck,
        // 后端 autoGenerate 默认 True(创建即调度 CF 构建),前端不再传该参数。
        // 目标场景集始终透传（生成完成后自动入集）
        sceneSetNodeId,
        // 保密标：仅在有 manage 权限且勾选时透传；后端二次校验权限，无权限入参被忽略
        securityLevel: canManageSecurity && confidential ? 1 : undefined,
        genMode,
      });
      if (!resp) return;
      setCreateState(resp.state);
      if (resp.state.status === 'running' && resp.message.includes('运行中')) {
        message.warning(resp.message);
      } else {
        message.success(resp.message || '建场景任务已启动');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const [caseIds, setCaseIds] = useState('');
  const [caseFlowSubmitting, setCaseFlowSubmitting] = useState(false);
  const [caseFlowSceneSetNodeId, setCaseFlowSceneSetNodeId] = useState<number | undefined>();
  const [caseFlowConfidential, setCaseFlowConfidential] = useState(false);
  const [caseFlowRegion, setCaseFlowRegion] = useState<'cn' | 'ae' | 'sea'>('cn');
  const [caseFlowState, setCaseFlowState] = useState<CreateSceneFromCaseFlowState | null>(null);
  const [caseFlowStateLoading, setCaseFlowStateLoading] = useState(false);

  const fetchCaseFlowState = useCallback(async (silent = false) => {
    if (!silent) setCaseFlowStateLoading(true);
    try {
      const st = await getCreateSceneFromCaseFlowStatus();
      setCaseFlowState(st ?? { status: 'idle' });
      return st ?? { status: 'idle' as const };
    } finally {
      if (!silent) setCaseFlowStateLoading(false);
    }
  }, []);

  const handleCreateFromCaseFlow = async () => {
    const inputIds = caseIds
      .split(/[\s,;;，]+/)
      .map(k => k.trim())
      .filter(Boolean);
    if (inputIds.length === 0) {
      message.warning('请输入至少一个 Case ID');
      return;
    }
    if (inputIds.length > MAX_BATCH_CASES) {
      message.error(batchLimitMessage(inputIds.length));
      return;
    }
    setCaseFlowSubmitting(true);
    try {
      const ids = await resolveExistingCompleted(inputIds, caseFlowSceneSetNodeId, setCaseIds);
      if (!ids) return;
      const resp = await createSceneFromCaseFlow({
        caseIds: ids.join(','),
        // 后端 autoGenerate 默认 True(创建即调度 CF 构建),前端不再传该参数。
        // 目标场景集始终透传(生成完成后自动入集)
        sceneSetNodeId: caseFlowSceneSetNodeId,
        // 保密标:仅在有 manage 权限且勾选时透传;后端二次校验权限,无权限入参被忽略
        securityLevel: canManageSecurity && caseFlowConfidential ? 1 : undefined,
        // 区域路由: 'ae' 查 UAE, 'sea' 查新加坡, 'cn' 查国内 (默认); 不做跨区兜底
        region: caseFlowRegion,
        genMode,
      });
      if (!resp) return;
      setCaseFlowState(resp.state);
      if (resp.state.status === 'running' && resp.message.includes('运行中')) {
        message.warning(resp.message);
      } else {
        message.success(resp.message || '建场景任务已启动');
      }
    } finally {
      setCaseFlowSubmitting(false);
    }
  };

  /**
   * 取消 / 续跑批量建场景批次（两张卡共用，落在当前 tab 的状态上）。
   *
   * 后端把最新状态一起回给我们（省一次轮询），失败原因是后端给的中文原话，
   * 直接吐给用户即可。任务列表也刷一次——取消 / 续跑都会改 Job / Task 状态。
   */
  const applyBatchAction = async (
    kind: 'cancel' | 'resume',
    jobId: number,
    reload: () => Promise<unknown>,
    setState: (st: BatchState) => void,
  ) => {
    const res = kind === 'cancel'
      ? await cancelSceneBatch(jobId)
      : await resumeSceneBatch(jobId);
    if (!res.ok) {
      message.error(res.message);
      await reload();
      return;
    }
    message.success(res.message);
    if (res.state) setState(res.state as BatchState);
    else await reload();
    refreshPncJobs();
  };

  const handleCancelBatch = (jobId: number) => applyBatchAction(
    'cancel', jobId, fetchCreateState,
    st => setCreateState(st as CreateSceneFromJiraState),
  );
  const handleResumeBatch = (jobId: number) => applyBatchAction(
    'resume', jobId, fetchCreateState,
    st => setCreateState(st as CreateSceneFromJiraState),
  );
  const handleCancelCaseFlowBatch = (jobId: number) => applyBatchAction(
    'cancel', jobId, () => fetchCaseFlowState(true),
    st => setCaseFlowState(st as CreateSceneFromCaseFlowState),
  );
  const handleResumeCaseFlowBatch = (jobId: number) => applyBatchAction(
    'resume', jobId, () => fetchCaseFlowState(true),
    st => setCaseFlowState(st as CreateSceneFromCaseFlowState),
  );

  const fetchScanState = useCallback(async (silent = false) => {
    if (!silent) setScanStateLoading(true);
    try {
      const st = await getScanJiraDaysStatus();
      setScanState(st ?? { status: 'idle' });
    } finally {
      if (!silent) setScanStateLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScanState(true);
    fetchCreateState(true);
    fetchCaseFlowState(true);
  }, [fetchScanState, fetchCreateState, fetchCaseFlowState]);

  // 加载场景树（供「目标场景集」下拉用）；失败不阻断主流程，仅留空下拉。
  useEffect(() => {
    let cancelled = false;
    getSceneTree(false)
      .then(nodes => { if (!cancelled) setSceneTree(nodes ?? []); })
      .catch(() => { if (!cancelled) setSceneTree([]); });
    return () => { cancelled = true; };
  }, []);

  /** 嵌套场景树 → TreeSelect treeData：目录(nodeFlag=0)仅作分组不可选，
   *  场景集(nodeFlag=1)可选；锁定节点禁用；隐藏节点剔除。
   *  注意：getSceneTree 返回的是带 children 的嵌套结构（非扁平 parentId 列表）。 */
  const sceneSetTreeData = useMemo(() => {
    const build = (nodes: SceneTreeNode[]): Record<string, unknown>[] =>
      nodes
        .filter(node => !node.isHidden)
        .map(node => {
          const isSet = node.nodeFlag === 1;
          const kids = node.children?.length ? build(node.children) : undefined;
          return {
            title: node.name,
            value: node.id,
            key: node.id,
            // 目录不可选（仅展开导航）；场景集可选，锁定的场景集禁用
            selectable: isSet && !node.isLocked,
            disabled: isSet ? !!node.isLocked : false,
            children: kids,
          };
        });
    return build(sceneTree);
  }, [sceneTree]);

  /** running 时按 5s 轮询；切换到非 running 时刷新一次列表，告知用户后台已建好。
   *  已中断（interrupted）时降到 15s：这条流不会自己结束，等的是用户点「继续」，
   *  别为了它每 5s 打后端。 */
  useEffect(() => {
    if (createState?.status !== 'running') return;
    const delay = createState.interrupted ? 15000 : 5000;
    const timer = window.setInterval(async () => {
      const next = await fetchCreateState(true);
      if (next && next.status !== 'running') {
        if (next.status === 'completed') {
          const r = next.result;
          const failed = r?.failed ?? 0;
          if (r?.cancelled) {
            message.info('本批已取消，未处理的条目不再构建');
          } else if (failed > 0) {
            // 逐条失败原因交给卡片底部的「失败明细」弹窗展示/下载，toast 只报汇总，
            // 避免几百条失败时把 message 撑爆。
            message.warning(
              `${r?.success ?? 0} 个新建，${r?.updated ?? 0} 个已更新，${failed} 个失败；`
              + '失败原因可在卡片底部查看或下载',
            );
          } else {
            const parts: string[] = [];
            if (r?.success) parts.push(`${r.success} 个新建`);
            if (r?.updated) parts.push(`${r.updated} 个已更新`);
            message.success(parts.length ? parts.join('，') : `${r?.total ?? 0} 个处理完成`);
          }
        } else if (next.status === 'failed') {
          message.error(next.error || '建场景任务失败');
        }
        refreshPncJobs();
      }
    }, delay);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createState?.status, createState?.interrupted]);

  /** case-flow running 时按 5s 轮询;完成后弹结果摘要并刷新列表(对齐 Jira 卡)。 */
  useEffect(() => {
    if (caseFlowState?.status !== 'running') return;
    const delay = caseFlowState.interrupted ? 15000 : 5000;
    const timer = window.setInterval(async () => {
      const next = await fetchCaseFlowState(true);
      if (next && next.status !== 'running') {
        if (next.status === 'completed') {
          const r = next.result;
          const failed = r?.failed ?? 0;
          if (r?.cancelled) {
            message.info('本批已取消，未处理的条目不再构建');
          } else if (failed > 0) {
            // 同 Jira 分支：逐条原因在卡片「失败明细」弹窗查看/下载，toast 只报汇总。
            message.warning(
              `${r?.success ?? 0} 个新建,${r?.updated ?? 0} 个已更新,${failed} 个失败;`
              + '失败原因可在卡片底部查看或下载',
            );
          } else {
            const parts: string[] = [];
            if (r?.success) parts.push(`${r.success} 个新建`);
            if (r?.updated) parts.push(`${r.updated} 个已更新`);
            message.success(parts.length ? parts.join(',') : `${r?.total ?? 0} 个处理完成`);
          }
        } else if (next.status === 'failed') {
          message.error(next.error || 'Case-Flow 建场景任务失败');
        }
        refreshPncJobs();
      }
    }, delay);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseFlowState?.status, caseFlowState?.interrupted]);

  /** Jira 扫描 running 时按 5s 轮询：让 「已扫描 X / Y」 实时刷新；
   *  完成后弹一次结果摘要并刷新场景列表，跟从 Jira 建场景路径行为对齐。 */
  useEffect(() => {
    if (scanState?.status !== 'running') return;
    const timer = window.setInterval(async () => {
      await fetchScanState(true);
      // 这里取最新值再判终态（fetchScanState 内部已 setState；用回调式读最新）
      setScanState((curr) => {
        if (!curr) return curr;
        if (curr.status === 'completed') {
          const r = curr.result;
          const parts: string[] = [];
          parts.push(`扫描 ${r?.jiraIssueCount ?? 0} 个 Issue`);
          if (r?.success) parts.push(`${r.success} 新建`);
          if (r?.skipped) parts.push(`${r.skipped} 跳过`);
          if (r?.failed) parts.push(`${r.failed} 失败`);
          message.success(parts.join('，'));
          refreshPncJobs();
        } else if (curr.status === 'failed') {
          message.error(curr.error || 'Jira 扫描任务失败');
        }
        return curr;
      });
    }, 5000);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanState?.status]);

  const handleScanJiraDays = async () => {
    setScanSubmitting(true);
    try {
      const resp = await scanJiraDaysAndCreateScenes({ scanDays, projects: scanProjects });
      if (!resp) return;
      setScanState(resp.state);
      if (resp.state.status === 'running' && resp.message.includes('运行中')) {
        message.warning(resp.message);
      } else {
        message.success(resp.message || '扫描任务已启动');
      }
    } finally {
      setScanSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '24px 32px' }}>
      <Title level={2} style={{ marginBottom: 4 }}>场景生成</Title>
      <Text type="secondary">规划入库与感知入库分开提交、分开看任务。</Text>
      <Tabs
        style={{ marginTop: 16 }}
        activeKey={activeTab}
        onChange={key => {
          const next = parseSceneGenerationTab(key);
          const params = new URLSearchParams(searchParams);
          if (next === 'pnc') params.delete('tab');
          else params.set('tab', next);
          setSearchParams(params, { replace: true });
        }}
        items={[
          { key: 'pnc', label: '规划场景', children: (
            <>
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginTop: 24,
          alignItems: 'stretch',
          flexWrap: 'wrap',
        }}
      >
        {/* 场景标注入口:一期后端 annotate-submit 接口未落地,先隐藏不对外暴露。
            页面本身、单场景编辑链路(/scene-annotate?sceneId=)继续保留,
            走原 batch-update 接口完成编辑。二期后端就绪后放开本卡片即可。 */}
        {false && (
          <SceneAnnotateCard
            sceneSetTreeData={sceneSetTreeData}
            canManageSecurity={canManageSecurity}
          />
        )}
        <BatchGenerateCard
          source={batchSource}
          onSourceChange={setBatchSource}
          genMode={genMode}
          onGenModeChange={setGenMode}
          inputValue={batchSource === 'jira' ? jiraKey : caseIds}
          onInputChange={batchSource === 'jira' ? setJiraKey : setCaseIds}
          canManageSecurity={canManageSecurity}
          confidential={batchSource === 'jira' ? confidential : caseFlowConfidential}
          onConfidentialChange={batchSource === 'jira' ? setConfidential : setCaseFlowConfidential}
          region={caseFlowRegion}
          onRegionChange={setCaseFlowRegion}
          sceneSetTreeData={sceneSetTreeData}
          sceneSetNodeId={batchSource === 'jira' ? sceneSetNodeId : caseFlowSceneSetNodeId}
          onSceneSetChange={batchSource === 'jira' ? setSceneSetNodeId : setCaseFlowSceneSetNodeId}
          state={batchSource === 'jira' ? createState : caseFlowState}
          stateLoading={batchSource === 'jira' ? createStateLoading : caseFlowStateLoading}
          onRefresh={() => (batchSource === 'jira' ? fetchCreateState() : fetchCaseFlowState())}
          submitting={batchSource === 'jira' ? submitting : caseFlowSubmitting}
          onSubmit={batchSource === 'jira' ? handleSubmit : handleCreateFromCaseFlow}
          onCancelBatch={batchSource === 'jira' ? handleCancelBatch : handleCancelCaseFlowBatch}
          onResumeBatch={batchSource === 'jira' ? handleResumeBatch : handleResumeCaseFlowBatch}
        />

        <ScanJiraCard
          scanDays={scanDays}
          onScanDaysChange={setScanDays}
          projects={scanProjects}
          onProjectsChange={setScanProjects}
          state={scanState}
          stateLoading={scanStateLoading}
          onRefresh={() => fetchScanState()}
          submitting={scanSubmitting}
          onSubmit={handleScanJiraDays}
        />
      </div>

      <SceneGenJobList taskTypeName="SCENE" refreshToken={pncJobRefresh} />
      <ForeignSyncTaskListCard />
            </>
          ) },
          { key: 'perception', label: '感知场景', children: (
            <>
              <PerceptionIngestCard
                canManageSecurity={canManageSecurity}
                onFinished={() => setPerceptionJobRefresh(n => n + 1)}
              />
              <SceneGenJobList
                taskTypeName="SCENE_PE"
                title="感知场景生成任务"
                refreshToken={perceptionJobRefresh}
              />
            </>
          ) },
        ]}
      />
      <ExistingCompletedModal
        items={existingPrompt?.items ?? []}
        canJoinSet={existingPrompt?.canJoinSet ?? false}
        modeLabel={genMode === 'cloud_cmd' ? '云控' : '事故'}
        onChoose={choice => {
          existingPrompt?.resolve(choice);
          setExistingPrompt(null);
        }}
      />
    </div>
  );
};

export default SceneGenerationPage;
